import { useState, useRef, useEffect } from "react";
import { Icon } from "./Icons";
import { DarkToggle } from "./DarkToggle";
import { api } from "../../api";
import gsap from "gsap";
import { ClinicIllustration, ToolsIllustration, DeliveryIllustration } from "./AuthIllustrations";
import { auth, googleProvider } from "../../firebase";
import { signInWithPopup } from "firebase/auth";

const SLIDES = [
  { Illustration: ClinicIllustration, title: "Scale Your Clinic", desc: "Join thousands of verified practices sourcing top-tier materials." },
  { Illustration: ToolsIllustration, title: "Premium Tools", desc: "Access India's largest B2B dental marketplace effortlessly." },
  { Illustration: DeliveryIllustration, title: "Guaranteed Delivery", desc: "Experience 24-hour dispatch logistics for critical supplies." }
];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const AnimatedLockIcon = () => {
  const ringsRef = useRef(null);
  const lockRef = useRef(null);

  useEffect(() => {
    gsap.to(ringsRef.current, { scale: 1.15, opacity: 0.5, duration: 2, repeat: -1, yoyo: true, ease: "sine.inOut" });
    gsap.to(lockRef.current, { y: -6, duration: 1.8, repeat: -1, yoyo: true, ease: "power1.inOut" });
  }, []);

  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
      <svg width="80" height="80" viewBox="0 0 100 100" fill="none">
        <g ref={ringsRef} transform="translate(50,50)">
          <circle cx="0" cy="0" r="32" fill="var(--blue)" opacity="0.1" />
          <circle cx="0" cy="0" r="42" fill="none" stroke="var(--blue)" strokeWidth="1" opacity="0.3" strokeDasharray="4 4" />
        </g>
        <g ref={lockRef} transform="translate(35,30)">
          <path d="M 5 15 L 5 10 C 5 -5, 25 -5, 25 10 L 25 15" stroke="var(--blue)" strokeWidth="4" strokeLinecap="round" fill="none" />
          <rect x="0" y="15" width="30" height="24" rx="6" fill="#ffffff" stroke="var(--blue)" strokeWidth="3" />
          <circle cx="15" cy="24" r="3" fill="var(--blue)" />
          <path d="M 14 26 L 16 26 L 17 32 L 13 32 Z" fill="var(--blue)" />
        </g>
        <path d="M 20 20 L 23 25 L 28 28 L 23 31 L 20 36 L 17 31 L 12 28 L 17 25 Z" fill="var(--cyan)" opacity="0.6" />
      </svg>
    </div>
  );
};

const AnimatedMailIcon = () => {
  const iconRef = useRef(null);
  const sparkRef = useRef(null);
  const letterRef = useRef(null);

  useEffect(() => {
    // Floating envelope
    gsap.to(iconRef.current, {
      y: -8, duration: 1.5, repeat: -1, yoyo: true, ease: "power1.inOut"
    });
    // Rotating/scaling spark
    gsap.to(sparkRef.current, {
      rotation: 180, scale: 1.25, duration: 2.5, repeat: -1, yoyo: true, ease: "sine.inOut"
    });
    // Letter sliding out slightly back and forth
    gsap.fromTo(letterRef.current, 
      { y: 8 },
      { y: 0, duration: 1.2, repeat: -1, yoyo: true, ease: "power1.inOut" }
    );
  }, []);

  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 24, marginTop: -20 }}>
      <svg width="90" height="90" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="42" fill="#f3f4f6" />
        
        {/* Rotating Sparkle */}
        <path ref={sparkRef} d="M78 22 L80 28 L86 30 L80 32 L78 38 L76 32 L70 30 L76 28 Z" fill="#0047AB" style={{ transformOrigin: "78px 30px" }}/>
        <circle cx="28" cy="25" r="3" fill="#0891B2" opacity="0.5" />
        <circle cx="82" cy="70" r="2" fill="#0047AB" opacity="0.3" />

        <g ref={iconRef}>
          {/* Back flap */}
          <path d="M22 45 L50 62 L78 45 L78 35 C78 32.8 76.2 31 74 31 L26 31 C23.8 31 22 32.8 22 35 Z" fill="#bae6fd" opacity="0.5"/>
          
          {/* Letter sliding out */}
          <g ref={letterRef}>
             <rect x="30" y="28" width="40" height="30" rx="4" fill="#ffffff" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.05))"/>
             <line x1="38" y1="36" x2="62" y2="36" stroke="#0047AB" strokeWidth="3" strokeLinecap="round" opacity="0.15" />
             <line x1="38" y1="44" x2="52" y2="44" stroke="#0047AB" strokeWidth="3" strokeLinecap="round" opacity="0.15" />
             <circle cx="58" cy="44" r="2.5" fill="#0891B2" />
          </g>
          
          {/* Envelope Front */}
          <path d="M22 45 L50 62 L78 45 L78 69 C78 71.2 76.2 73 74 73 L26 73 C23.8 73 22 71.2 22 69 Z" fill="#0047AB" />
          {/* Envelope Flap Fold */}
          <path d="M22 45 L50 62 L78 45 Z" fill="#0891B2" opacity="0.95"/>
        </g>
      </svg>
    </div>
  );
};

export function SignupPage({ onBack, onSignupComplete, dark, setDark }) {
  const [step, setStep]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [serverErr, setServerErr] = useState("");
  const [otpErr, setOtpErr]     = useState("");
  const [enteredOtp, setEnteredOtp] = useState("");
  const [resendMsg, setResendMsg]   = useState("");
  const [fieldErr, setFieldErr] = useState({});
  const [showPass, setShowPass] = useState(false);
  
  const [form, setForm] = useState({ name:"", email:"", phone:"", clinic:"", city:"", password:"" });
  const [agreed, setAgreed] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [slide, setSlide] = useState(0);

  const cardRef = useRef(null);
  const leftRef = useRef(null);
  const formRef = useRef(null);
  const imgRef = useRef(null);
  const slideContentRef = useRef(null);
  const successRef = useRef(null);

  // 1. Initial Spring Entrance Animations
  useEffect(() => {
    gsap.fromTo(cardRef.current, 
      { y: 40, opacity: 0, scale: 0.95 },
      { y: 0, opacity: 1, scale: 1, duration: 1.2, ease: "elastic.out(1, 0.75)" }
    );
    gsap.fromTo(leftRef.current,
      { x: -30, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.1 }
    );
    const formEls = formRef.current?.children;
    if (formEls) {
      gsap.fromTo(formEls,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.05, ease: "back.out(1.2)", delay: 0.2 }
      );
    }
  }, []);

  // 2. Interactive Feed Rotation
  useEffect(() => {
    if (imgRef.current) {
      if (isFocused) gsap.to(imgRef.current, { rotation: 2, scale: 1.04, duration: 0.7, ease: "elastic.out(1, 0.6)" });
      else gsap.to(imgRef.current, { rotation: 0, scale: 1, duration: 0.5, ease: "power3.out" });
    }
  }, [isFocused]);

  // 3. Carousel
  useEffect(() => {
    const timer = setInterval(() => {
      gsap.to(slideContentRef.current, { opacity: 0, y: -10, duration: 0.3, onComplete: () => {
        setSlide(s => (s + 1) % SLIDES.length);
        gsap.fromTo(slideContentRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" });
      }});
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // 4. Success Modal Pop
  useEffect(() => {
    if (step === 3 && successRef.current) {
      gsap.fromTo(successRef.current, { scale: 0.8, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.8, ease: "elastic.out(1, 0.6)" });
    }
  }, [step]);

  const upd = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setFieldErr(e => ({ ...e, [k]: "" }));
    setServerErr("");
  };

  const handleSocialLogin = async (provider) => {
    setServerErr(""); setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      const res = await api("/api/auth/firebase", {
        method: "POST", auth: false,
        body: { token, rememberMe }
      });
      onSignupComplete(res.user, res.token, rememberMe);
    } catch (error) {
      console.error(error);
      if (error.code !== "auth/popup-closed-by-user" && error.code !== "auth/cancelled-popup-request") {
        setServerErr(error.message || "Social login failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.password) e.password = "Password required";
    else if (form.password.length < 6) e.password = "Min 6 chars";
    // For mobile/minimal dribbble form, I've reduced initial fields to match graphic. City/Clinic handled inside backend optionally or expanded later.
    
    if (!agreed) setServerErr("You must agree to the Terms of Service.");
    
    setFieldErr(e);
    return Object.keys(e).length === 0 && agreed;
  };

  const handleSendOtp = async () => {
    if (!validate()) return;
    setLoading(true); setServerErr("");
    try {
      await api("/api/auth/signup", { method:"POST", auth:false, body:{
        name: form.name.trim(), email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || "0000000000", clinic: form.clinic.trim() || "Pending Details",
        city: form.city.trim() || "India", password: form.password,
      }});
      setStep(2);
    } catch (err) {
      setServerErr(err.message || "Failed to register.");
    } finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (enteredOtp.length < 6) return;
    setLoading(true); setOtpErr("");
    try {
      const { user, token } = await api("/api/auth/verify", { method:"POST", auth:false, body:{
        email: form.email.trim().toLowerCase(), otp: enteredOtp.trim(), rememberMe
      }});
      setStep(3);
      setTimeout(() => onSignupComplete(user, token, rememberMe), 3000);
    } catch (err) {
      setOtpErr(err.message || "Verification failed.");
    } finally { setLoading(false); }
  };

  if (step === 3) return (
    <div className="clean-auth-page">
      <div className="clean-auth-card" style={{ maxWidth: 600, minHeight: 400, display: "flex", alignItems: "center", justifyContent: "center" }} ref={successRef}>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: "var(--blue-l)", color: "var(--blue)", display: "flex", alignItems:"center", justifyContent:"center", margin: "0 auto 24px" }}>
            <Icon name="check" size={40} />
          </div>
          <h2 style={{ fontFamily: "var(--font-head)", fontSize: 32, fontWeight: 800, marginBottom: 12, color: "var(--text1)" }}>Success!</h2>
          <p style={{ color: "var(--text3)", lineHeight: 1.6, marginBottom: 30 }}>Your clinic profile was uniquely verified.<br/>Connecting you securely to the K-Dental network...</p>
          <Icon name="loader" size={24} className="spin" style={{ color: "var(--blue)" }}/>
        </div>
      </div>
    </div>
  );

  return (
    <div className="clean-auth-page">
      <div className="clean-auth-card" ref={cardRef}>
        
        <div className="clean-left" ref={leftRef}>
          <div className="clean-carousel">
            <div ref={slideContentRef} style={{ display:"flex", flexDirection:"column", alignItems:"center", height:"100%", justifyContent:"center" }}>
              <div ref={imgRef} className="clean-carousel-img">
                {(() => {
                  const CurrentIllustration = SLIDES[slide].Illustration;
                  return <CurrentIllustration />;
                })()}
              </div>
              <h3>{SLIDES[slide].title}</h3>
              <p>{SLIDES[slide].desc}</p>
            </div>
            <div className="clean-dots">
              {SLIDES.map((_, i) => <div key={i} className={`clean-dot ${i === slide ? "active" : ""}`} />)}
            </div>
          </div>
        </div>

        <div className="clean-right">
          <div className="clean-topbar">
            <div className="clean-brand"><img src="/logo.png" alt="Logo" /> K Dental</div>
            <div style={{ display:"flex", alignItems:"center", gap: 24 }}>
              <DarkToggle dark={dark} setDark={setDark}/>
              <button className="clean-top-link" onClick={onBack}>Log in instead</button>
            </div>
          </div>

          <div className="clean-right-content" ref={formRef}>
            {step === 1 ? <AnimatedLockIcon /> : null}
            <h2 style={{ textAlign: "center" }}>{step === 1 ? "Register Now" : "Email Verification"}</h2>

            {step === 1 && <>
              <div className="social-btns" style={{ marginBottom: 30, display: "flex", justifyContent: "center" }}>
                <button className="social-btn" onClick={() => handleSocialLogin(googleProvider)} disabled={loading} style={{ width: "100%", maxWidth: 280 }}><GoogleIcon /> Google</button>
              </div>

              <div className="clean-divider">Or Register With</div>

              {serverErr && <div className="clean-err-msg" style={{ marginBottom:14 }}><Icon name="warning" size={13}/> {serverErr}</div>}

              <div className="clean-field">
                <input type="email" placeholder="Email address" value={form.email}
                  onChange={e => upd("email", e.target.value)}
                  onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                  className={fieldErr.email ? "error" : ""} />
              </div>
              
              <div className="clean-field">
                <input type="text" placeholder="Full name / Clinic" value={form.name}
                  onChange={e => upd("name", e.target.value)}
                  onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                  className={fieldErr.name ? "error" : ""} />
              </div>

              <div className="clean-field">
                <div style={{ position: "relative" }}>
                  <input type={showPass ? "text" : "password"} placeholder="Password" value={form.password}
                    onChange={e => upd("password", e.target.value)}
                    onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                    className={fieldErr.password ? "error" : ""} style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowPass(v=>!v)} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>
                    <Icon name={showPass ? "eyeOff" : "eye"} size={16}/>
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "var(--text3)", userSelect: "none" }}>
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ cursor: "pointer", width: 14, height: 14, accentColor: "var(--blue)" }} />
                  Remember me
                </label>
              </div>

              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 10, cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ width: 18, height: 18, accentColor: "var(--blue)", flexShrink: 0, marginTop: 2, borderRadius: 6 }} />
                <span style={{ fontSize: 13, color: "var(--text3)", lineHeight: 1.5 }}>
                  By registering you agree to K Dental's Terms of Service and Privacy Policy.
                </span>
              </label>

              <button className="clean-btn" onClick={handleSendOtp} disabled={loading} style={{ marginTop: 24 }}>
                {loading ? "Processing…" : "Process now"}
              </button>
            </>}

            {step === 2 && <>
              <AnimatedMailIcon />
              <p style={{ color: "var(--text3)", fontSize: 14, marginBottom: 30, lineHeight: 1.6, textAlign: "center" }}>
                We sent a secure verification code to <strong style={{color:"var(--text1)"}}>{form.email}</strong>.<br/>Please enter it below.
              </p>
              
              <div className="clean-field">
                <input type="text" inputMode="numeric" maxLength={6} placeholder="• • • • • •" value={enteredOtp}
                  onChange={e => { setEnteredOtp(e.target.value.replace(/\D/g,"").slice(0,6)); setOtpErr(""); }}
                  onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                  onKeyDown={e => e.key === "Enter" && handleVerify()}
                  className={otpErr ? "error" : ""}
                  style={{ fontSize: 24, letterSpacing: 16, textAlign: "center", fontWeight: 700 }} />
                {otpErr && <div className="clean-err-msg"><Icon name="warning" size={13}/> {otpErr}</div>}
              </div>

              <button className="clean-btn" onClick={handleVerify} disabled={enteredOtp.length < 6 || loading} style={{ marginTop: 20 }}>
                {loading ? "Verifying…" : "Confirm Identity"}
              </button>

              <p style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "var(--text3)" }}>
                Didn't receive it? <span style={{ color: "var(--blue)", cursor: "pointer", fontWeight: 600 }} onClick={async () => {
                  try {
                    await api("/api/auth/resend", { method:"POST", auth:false, body:{ email: form.email.trim().toLowerCase() }});
                    alert("Code resent!");
                  } catch (e) { alert("Failed to resend code."); }
                }}>Resend code</span>
              </p>
            </>}
            
          </div>
        </div>

      </div>
    </div>
  );
}
